/**
 * Environment detection and switching.
 *
 * Determines whether the web app is running in staging or production
 * based on hostname, with a localStorage override for manual switching.
 */

export type Environment = 'production' | 'staging';

const ENV_OVERRIDE_KEY = 'talkyn-env-override';

/** API base URLs per environment */
const API_URLS: Record<Environment, string> = {
  production: 'https://api.talkyn.ai.xxz.dk',
  staging: 'https://api-staging.talkyn.ai.xxz.dk',
};

/** Frontend hostnames per environment */
const HOSTNAMES: Record<string, Environment> = {
  'talkyn.ai.xxz.dk': 'production',
  'staging.talkyn.ai.xxz.dk': 'staging',
};

/**
 * Detect the natural environment from hostname.
 * Defaults to staging for unknown hosts (localhost, etc).
 */
export function detectEnvironment(): Environment {
  const host = window.location.hostname;
  return HOSTNAMES[host] ?? 'staging';
}

/**
 * Get the manually overridden environment, if any.
 */
export function getEnvironmentOverride(): Environment | null {
  const override = localStorage.getItem(ENV_OVERRIDE_KEY);
  if (override === 'production' || override === 'staging') return override;
  return null;
}

/**
 * Set a manual environment override.
 * Pass null to clear the override and use hostname detection.
 */
export function setEnvironmentOverride(env: Environment | null): void {
  if (env === null) {
    localStorage.removeItem(ENV_OVERRIDE_KEY);
  } else {
    localStorage.setItem(ENV_OVERRIDE_KEY, env);
  }
}

/**
 * Get the active environment (override takes precedence over hostname detection).
 */
export function getEnvironment(): Environment {
  return getEnvironmentOverride() ?? detectEnvironment();
}

/**
 * Whether the user has manually overridden the environment.
 */
export function isEnvironmentOverridden(): boolean {
  return getEnvironmentOverride() !== null;
}

/**
 * Get the API base URL for the active environment.
 * Legacy custom URL override from Settings takes highest priority.
 */
export function getApiUrl(): string {
  // Legacy custom URL override from Settings takes highest priority
  const customUrl = localStorage.getItem('voice-ai-edge-tts-url');
  if (customUrl) return customUrl;

  return API_URLS[getEnvironment()];
}

/**
 * Get environment display info for the UI badge.
 * Returns null if running in production without override (clean UI).
 */
export function getEnvironmentBadge(): { label: string; color: string } | null {
  const env = getEnvironment();
  const overridden = isEnvironmentOverridden();
  const natural = detectEnvironment();

  // Production with no override: clean UI, no badge
  if (env === 'production' && !overridden) return null;

  // Staging (natural or overridden): show badge
  if (env === 'staging') {
    return {
      label: natural === 'staging' ? 'BETA' : 'STAGING',
      color: '#ff6b35',
    };
  }

  // Production via override (was staging, switched to prod): show badge
  if (env === 'production' && overridden) {
    return {
      label: 'PROD',
      color: '#22c55e',
    };
  }

  return null;
}
